// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// Rxjs imports
import { Observable, forkJoin } from 'rxjs';

// devextreme imports
import { DxDataGridComponent, DxFileUploaderComponent } from 'devextreme-angular';

// cc-common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Component imports
import { PropertyCommunityDocumentsComponent } from '../../../management-common/component/common/property-community-documents/property-community-documents.component';

// Constant imports
import { ActionConstants } from '../../../_constants/action-constants';
import { DocumentTypeConstant } from '../../../_constants/document-type-constants';
import { EmailConstants } from '../../../_constants/email-constants';

// Service imports
import { FileReaderService } from '../../../_services/shared-services/file-reader.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { SharedAccApplicationService } from '../../_services/shared-acc-application.service';

// Model imports
import { EmailAttachmentRawDto } from '../../../ciramail-module/_models/email-composer/email-attachment-raw-dto';
import { AccAddDocumentsParameterDto } from '../../_models/acc-add-documents-parameter-dto';
import { AccAttachmentRawDto } from '../../_models/acc-attachment-raw-dto';
import { AccDocumentAttachmentParameterDto } from '../../_models/acc-document-attachment-parameter-dto';
import { AccDocumentDetailParametersDto } from '../../_models/acc-document-detail-parameters-dto';
import { AccDocumentDownloadParameterDto } from '../../_models/acc-document-download-parameter-dto';
import { AccDocumentUnlinkParameterDto } from '../../_models/acc-document-unlink-parameter-dto';
import { AccDocumentDetailsResultDto } from '../../_models/acc-documents-details-result-dto';

@Component({
    selector: 'app-architectural-application-attachment-editor',
    templateUrl: './architectural-application-attachment-editor.component.html',
    styleUrls: ['./architectural-application-attachment-editor.component.scss']
})
export class ArchitecturalApplicationAttachmentEditorComponent implements OnInit {
    @ViewChild('projectFileUploader', { static: false }) projectFileUploader: DxFileUploaderComponent;
    @ViewChild('itemfileUploader', { static: false }) itemfileUploader: DxFileUploaderComponent;

    @ViewChild('propertyCommunityDocuments', { static: false })
    propertyCommunityDocuments: PropertyCommunityDocumentsComponent;
    @ViewChild('attachmentGrid', { static: false }) attachmentGrid: DxDataGridComponent;
    @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;

    @Input() visible = false;
    @Input() customerId: string;
    @Input() docParam: AccDocumentDetailParametersDto;
    @Input() communityId: number;
    @Input() projectAttachments: AccAttachmentRawDto[] = [];
    @Input() lineItemAttachment: AccAttachmentRawDto[] = [];
    @Input() isProjectDocs: boolean;
    @Input() isItemDocs: boolean;
    @Input() projectAccItemId: number;
    @Input() lineItemId: number;
    @Input() emailAttachments: Array<EmailAttachmentRawDto> = [];
    @Input() isViewOnly = false;

    @Output() onClose = new EventEmitter<any>();
    @Output() onDocumentUpdate = new EventEmitter<any>();

    acceptedFileTypes = [
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'text/csv',
        'image/jpeg',
        'image/png',
        'image/bmp'
    ];

    loadingVisible = false;
    selectedAttachments: AccDocumentDetailsResultDto = new AccDocumentDetailsResultDto();
    selectedProjectAttachments: AccAttachmentRawDto[] = [];
    selectedItemAttachments: AccAttachmentRawDto[] = [];

    iconClose = IconConstants.CLOSE;
    iconUnlink = IconConstants.UNLINK;
    iconCheck = IconConstants.CHECK;
    iconAdd = IconConstants.ADD;

    attachLocal = EmailConstants.ATTACH_LOCAL_FILE;
    attachOwner = EmailConstants.ATTACH_OWNER_DOC;
    attachEmail = EmailConstants.ATTACH_EMAIL_DOC;

    itemDoc = DocumentTypeConstant.ITEM_DOCS;
    projectDoc = DocumentTypeConstant.PROJECT_DOCS;

    selectedAttachmentType: string; // This will hold type of doc either "Project Docs" or "Item Docs"

    isRefreshGrid = false;

    showEmailAttachmentSelector = false;

    constructor(
        private readonly helperService: HelperService,
        private readonly fileReaderService: FileReaderService,
        private readonly accApplicationService: SharedAccApplicationService,
        private readonly errorService: ServerErrorService
    ) {}

    ngOnInit() {
        if (Utils.notNullAndDefined(this.docParam)) {
            this.populateAccDocumentDetail();
        } else {
            this.projectAttachments.forEach((p, i) => {
                p.id = i + 1;
            });
            this.lineItemAttachment.forEach((item, i) => {
                item.id = i + 1;
            });

            this.selectedProjectAttachments = this.projectAttachments;
            this.selectedItemAttachments = this.lineItemAttachment;
        }
    }

    getFileIcon(record: any): string {
        const fileName = record.fileName;
        return this.helperService.getFileIconByExtension(fileName);
    }

    get acceptedFileUploadTypes(): string {
        return this.acceptedFileTypes.join(',');
    }

    onCloseClick(): void {
        this.closeEditor();
    }

    onDownloadFile(file: any, attachmentType: string): void {
        this.selectedAttachmentType = attachmentType;
        if (Utils.notNullAndDefined(file.fileId)) {
            this.downloadFile(file);
        } else if (file instanceof File) {
            const blob: Blob = new Blob([file]);
            this.helperService.downloadFileUsingBlob(blob, file.name);
        }
    }

    onLocalAttachment(attachmentType: string): void {
        if (Utils.equalsIgnoreCase(attachmentType, DocumentTypeConstant.ITEM_DOCS)) {
            if (
                Utils.notNullAndDefined(this.itemfileUploader) &&
                Utils.notNullAndDefined(this.itemfileUploader.instance)
            ) {
                const uploader: any = this.itemfileUploader.instance;
                uploader._isCustomClickEvent = true;
                uploader._$fileInput[0].click();
            }
        } else {
            if (
                Utils.notNullAndDefined(this.projectFileUploader) &&
                Utils.notNullAndDefined(this.projectFileUploader.instance)
            ) {
                const uploader: any = this.projectFileUploader.instance;
                uploader._isCustomClickEvent = true;
                uploader._$fileInput[0].click();
            }
        }
    }

    onAttachmentsSelect(event: any, attachmentType: string): void {
        const localAttachments = event.value;
        const validLocalAttachments = [];

        localAttachments.forEach((attachment) => {
            if (attachment.name.length <= 100) {
                validLocalAttachments.push(attachment);
            } else {
                this.messageBox.showErrorMessage(
                    'Error',
                    `Filename cannot exceed 100 characters. Current filename length: ${attachment.name.length}`
                );
            }
        });

        if (Utils.notNullOrEmpty(validLocalAttachments)) {
            if (Utils.notNullOrEmpty(event) && Utils.notNullOrEmpty(event.value)) {
                this.selectedAttachmentType = attachmentType;

                if (Utils.notNullAndDefined(this.docParam) && Utils.notNullOrEmpty(event.value)) {
                    // Build param dto
                    const param = new AccAddDocumentsParameterDto();
                    param.accItemId = this.docParam.accItemId;
                    param.communityId = this.docParam.communityId;
                    param.customerId = Utils.notNullAndDefined(this.docParam.customerId)
                        ? this.docParam.customerId
                        : this.customerId;

                    param.attachments = [];

                    // Add attachments in param dto
                    forkJoin(this.fileReaderService.readFiles(validLocalAttachments)).subscribe((value) => {
                        if (Utils.notNullOrEmpty(value) && Utils.notNullOrEmpty(value[0])) {
                            const files = value[0];

                            param.attachments.push(
                                ...files.map((f) => {
                                    const attachment = new AccDocumentAttachmentParameterDto();

                                    attachment.fileId = null;
                                    attachment.fileName = f.fileName;
                                    attachment.fileBase64String = f.attachmentBase64;

                                    return attachment;
                                })
                            );
                        }

                        // Upload attachments
                        this.uploadDocuments(param, attachmentType);
                    });
                } else {
                    // This block will be used only in Add new files in New/Edit ACC
                    validLocalAttachments.forEach((doc) => {
                        this.fileReaderService.readFileToBase64(doc).subscribe((base64Data) => {
                            const attachment: AccAttachmentRawDto = new AccAttachmentRawDto();
                            attachment.action = ActionConstants.ADD;
                            attachment.fileName = doc.name;
                            attachment.fileId = null;
                            attachment.emailCacheId = null;
                            attachment.fileBase64String = base64Data;
                            this.setProjectAndItemDocs(attachment, attachmentType);
                        });
                    });
                }

                this.resetFileUploader(attachmentType);
            }
        }
    }

    onDocumentAttachment(attachmentType: string): void {
        this.selectedAttachmentType = attachmentType;
        const customerId =
            Utils.notNullAndDefined(this.docParam) && Utils.notNullAndDefined(this.docParam.customerId)
                ? this.docParam.customerId
                : this.customerId;
        if (
            Utils.notNullAndDefined(this.docParam) &&
            Utils.notNullAndDefined(this.docParam.communityId) &&
            Utils.notNullAndDefined(customerId)
        ) {
            this.propertyCommunityDocuments.show(this.attachOwner, this.docParam.communityId, customerId, 0);
        } else {
            this.propertyCommunityDocuments.show(this.attachOwner, this.communityId, customerId, 0);
        }
    }

    onAttachOwnerDocuments(ownerFiles: any[]): void {
        if (Utils.notNullOrEmpty(ownerFiles)) {
            if (Utils.notNullAndDefined(this.docParam)) {
                // Build param dto
                const param = new AccAddDocumentsParameterDto();
                param.accItemId = this.docParam.accItemId;
                param.communityId = this.docParam.communityId;
                param.customerId = Utils.notNullAndDefined(this.docParam.customerId)
                    ? this.docParam.customerId
                    : this.customerId;

                param.attachments = [];

                // Add attachments
                ownerFiles.forEach((file) => {
                    const attachment = new AccDocumentAttachmentParameterDto();

                    attachment.fileId = file.fileId;
                    attachment.fileBase64String = null;
                    attachment.fileName = file.fileName;

                    // Add in param dto
                    param.attachments.push(attachment);
                });

                // Upload attachments
                this.uploadDocuments(param, this.selectedAttachmentType);
            } else {
                // This block will be used only in Add new files in New/Edit ACC
                ownerFiles.forEach((doc) => {
                    const attachment: AccAttachmentRawDto = new AccAttachmentRawDto();
                    attachment.action = ActionConstants.ADD;
                    attachment.fileName = doc.fileName;
                    attachment.fileId = doc.fileId;
                    attachment.emailCacheId = null;
                    attachment.fileBase64String = null;
                    this.setProjectAndItemDocs(attachment, this.selectedAttachmentType);
                });
            }
        }
    }

    onUnlinkDocument(data: any, attachmentType: string): void {
        if (Utils.notNullAndDefined(data) && Utils.notNullAndDefined(this.docParam)) {
            const dialog = this.helperService.getConfirmationDialog(
                'Confirmation',
                'Are you sure you want to remove this file?',
                'Yes',
                'No',
                'success'
            );

            dialog.show().then((answer) => {
                if (Utils.isTrue(answer)) {
                    this.unlinkExistingDocument(data, attachmentType);
                }
            });
        } else {
            // This block will be used only in Add new files in New/Edit ACC
            if (Utils.equalsIgnoreCase(attachmentType, DocumentTypeConstant.PROJECT_DOCS)) {
                data.action = Utils.notNullAndDefined(data.fileId) ? ActionConstants.DELETE : null;
                this.selectedProjectAttachments = this.selectedProjectAttachments.filter((p) =>
                    Utils.notEquals(p.id, data.id)
                ); // filtering out the doc to be deleted
            } else {
                data.action = Utils.notNullAndDefined(data.fileId) ? ActionConstants.DELETE : null;

                this.selectedItemAttachments = this.selectedItemAttachments.filter((item) =>
                    Utils.notEquals(item.id, data.id)
                ); // filtering out the doc to be deleted
            }
            this.setIdForAttachments(attachmentType);
        }
    }

    private unlinkExistingDocument(data: any, attachmentType: string): void {
        const param: AccDocumentUnlinkParameterDto = new AccDocumentUnlinkParameterDto();
        param.accItemId = this.docParam.accItemId;
        param.communityId = this.docParam.communityId;
        param.customerId = Utils.notNullAndDefined(this.docParam.customerId)
            ? this.docParam.customerId
            : this.customerId;
        param.fileId = data.fileId;
        param.clearCache = true;
        let uploadDocumentService: any;
        if (Utils.equals(attachmentType, DocumentTypeConstant.PROJECT_DOCS)) {
            uploadDocumentService = this.accApplicationService.unlinkProjectDocument(param);
        } else {
            uploadDocumentService = this.accApplicationService.unlinkItemDocument(param);
        }
        this.loadingVisible = true;
        uploadDocumentService.subscribe(
            () => {
                this.loadingVisible = false;
                this.isRefreshGrid = true;
                this.populateAccDocumentDetail();
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    onOkClick(): void {
        const projectAttachments = this.selectedProjectAttachments;
        if (Utils.isTrue(this.isProjectDocs)) {
            this.onDocumentUpdate.emit(projectAttachments);
            this.onClose.emit(true);
        } else {
            const lineItemAttachment = this.selectedItemAttachments;
            const docs: any = {
                projectDocs: projectAttachments,
                itemDocs: lineItemAttachment
            };
            this.onDocumentUpdate.emit(docs);
            this.onClose.emit(true);
        }
        this.visible = false;
    }

    onEmailDocumentAttachment(attachmentType: string): void {
        this.selectedAttachmentType = attachmentType;
        this.showEmailAttachmentSelector = true;
    }

    onAttachEmailAttachments(selectedEmailAttachments: EmailAttachmentRawDto[]): void {
        selectedEmailAttachments.forEach((emailAttachment) => {
            const attachment: AccAttachmentRawDto = new AccAttachmentRawDto();
            attachment.action = ActionConstants.ADD;
            attachment.fileName = emailAttachment.fileName;
            attachment.fileId = emailAttachment.fileId;
            attachment.emailCacheId = emailAttachment.emailCacheId;
            attachment.fileBase64String = emailAttachment.fileBase64String;
            this.setProjectAndItemDocs(attachment, this.selectedAttachmentType);
        });
    }

    onEmailAttachmentSelectorClose(): void {
        this.showEmailAttachmentSelector = false;
    }

    private resetFileUploader(attachmentType: string): void {
        if (Utils.equalsIgnoreCase(attachmentType, DocumentTypeConstant.PROJECT_DOCS)) {
            if (Utils.notNullAndDefined(this.projectFileUploader)) {
                this.projectFileUploader.instance.reset();
            }
        } else {
            if (Utils.notNullAndDefined(this.itemfileUploader)) {
                this.itemfileUploader.instance.reset();
            }
        }
    }

    private populateAccDocumentDetail(): void {
        this.loadingVisible = true;
        this.accApplicationService.getAccApplicationDocumentDetail(this.docParam).subscribe(
            (result: AccDocumentDetailsResultDto) => {
                this.selectedAttachments = result;

                const projectDocuments: Array<AccAttachmentRawDto> = [];
                if (Utils.notNullOrEmpty(this.selectedAttachments.projectDocuments)) {
                    this.selectedAttachments.projectDocuments.forEach((projectDoc) => {
                        const rawObj: AccAttachmentRawDto = new AccAttachmentRawDto();
                        rawObj.fileId = projectDoc.fileId;
                        rawObj.fileName = projectDoc.fileName;
                        rawObj.downloadUrl = projectDoc.downloadUrl;

                        projectDocuments.push(rawObj);
                    });
                }
                this.selectedProjectAttachments = projectDocuments;

                const itemDocuments: Array<AccAttachmentRawDto> = [];
                if (Utils.notNullOrEmpty(this.selectedAttachments.itemDocuments)) {
                    this.selectedAttachments.itemDocuments.forEach((itemDoc) => {
                        const rawObj: AccAttachmentRawDto = new AccAttachmentRawDto();
                        rawObj.fileId = itemDoc.fileId;
                        rawObj.fileName = itemDoc.fileName;
                        rawObj.downloadUrl = itemDoc.downloadUrl;

                        itemDocuments.push(rawObj);
                    });
                }
                this.selectedItemAttachments = itemDocuments;

                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private setIdForAttachments(attachmentType: string): void {
        if (Utils.equalsIgnoreCase(attachmentType, DocumentTypeConstant.PROJECT_DOCS)) {
            this.selectedProjectAttachments.forEach((item, index) => {
                item.id = index + 1;
            });
        } else {
            this.selectedItemAttachments.forEach((item, index) => {
                item.id = index + 1;
            });
        }
    }

    private getAttachmentIfDuplicate(
        attachment: AccAttachmentRawDto,
        attachments: Array<AccAttachmentRawDto>
    ): AccAttachmentRawDto {
        const duplicateFile = attachments.find((f) => Utils.equalsIgnoreCase(f.fileName, attachment.fileName));
        if (Utils.notNullAndDefined(duplicateFile)) {
            const fileNameSplitWithExtension = duplicateFile.fileName.split('.');
            if (fileNameSplitWithExtension.length === 2) {
                let duplicateFileName = `${fileNameSplitWithExtension[0]}_(1).${fileNameSplitWithExtension[1]}`;
                while (attachments.find((f) => Utils.equalsIgnoreCase(f.fileName, duplicateFileName))) {
                    const fNameSplitWithExtension = duplicateFileName.split('.');
                    duplicateFileName = `${fNameSplitWithExtension[0]}_(1).${fNameSplitWithExtension[1]}`;
                }
                attachment.fileName = duplicateFileName;
            }
        }

        return attachment;
    }

    private setProjectAndItemDocs(attachment: any, attachmentType: string): void {
        if (Utils.equalsIgnoreCase(attachmentType, DocumentTypeConstant.PROJECT_DOCS)) {
            if (Utils.notNullOrEmpty(this.selectedProjectAttachments)) {
                const attachmentToPush = this.getAttachmentIfDuplicate(attachment, this.selectedProjectAttachments);
                this.selectedProjectAttachments.push(attachmentToPush);
            } else {
                this.selectedProjectAttachments.push(attachment);
            }
        } else {
            if (Utils.notNullOrEmpty(this.selectedItemAttachments)) {
                const attachmentToPush = this.getAttachmentIfDuplicate(attachment, this.selectedItemAttachments);
                this.selectedItemAttachments.push(attachmentToPush);
            } else {
                this.selectedItemAttachments.push(attachment);
            }
        }
        this.setIdForAttachments(attachmentType);
    }

    private uploadDocuments(param: AccAddDocumentsParameterDto, attachmentType: string): void {
        // Show loader
        this.loadingVisible = true;

        let uploadDocumentService: any;

        if (Utils.equals(attachmentType, DocumentTypeConstant.PROJECT_DOCS)) {
            uploadDocumentService = this.accApplicationService.uploadProjectDocuments(param);
        } else {
            uploadDocumentService = this.accApplicationService.uploadItemDocuments(param);
        }

        // Make API call
        uploadDocumentService.subscribe(
            () => {
                // Hide loader
                this.loadingVisible = false;
                this.isRefreshGrid = true;

                // Refresh grid
                this.populateAccDocumentDetail();
            },
            (error) => {
                // Hide loader
                this.loadingVisible = false;

                // Display error
                this.errorService.displayError(error);
            }
        );
    }

    private downloadFile(file: any): void {
        if (
            Utils.notNullAndDefined(this.docParam) &&
            Utils.notNullAndDefined(file) &&
            Utils.notNullAndDefined(file.downloadUrl)
        ) {
            this.loadingVisible = true;
            this.helperService.downloadFileUsingUrl(file.downloadUrl, file.fileName, () => {
                this.loadingVisible = false;
            });
        } else {
            const param: AccDocumentDownloadParameterDto = new AccDocumentDownloadParameterDto();
            param.accItemId = Utils.equalsIgnoreCase(this.selectedAttachmentType, DocumentTypeConstant.PROJECT_DOCS)
                ? this.projectAccItemId
                : file.accItemId;
            param.fileId = file.fileId;
            param.communityId = this.communityId;
            param.customerId =
                Utils.notNullAndDefined(this.docParam) && Utils.notNullAndDefined(this.docParam.customerId)
                    ? this.docParam.customerId
                    : this.customerId;
            param.clearCache = true;
            let accDocumentService: Observable<any>;
            if (Utils.equalsIgnoreCase(this.selectedAttachmentType, DocumentTypeConstant.PROJECT_DOCS)) {
                accDocumentService = this.accApplicationService.downloadProjectDocument(param);
            } else {
                accDocumentService = this.accApplicationService.downloadItemDocument(param);
            }
            this.loadingVisible = true;
            accDocumentService.subscribe(
                (result: Blob) => {
                    this.helperService.downloadFileUsingBlob(result, file.fileName);
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    private closeEditor(): void {
        this.visible = false;
        if (Utils.isFalse(this.isRefreshGrid)) {
            this.projectAttachments = this.projectAttachments.filter((d) => Utils.isNullOrEmpty(d.action));
            this.selectedProjectAttachments = this.projectAttachments;
            this.lineItemAttachment = this.lineItemAttachment.filter((d) => Utils.isNullOrEmpty(d.action));
            this.selectedItemAttachments = this.lineItemAttachment;
        }
        this.onClose.emit(this.isRefreshGrid);
    }
}
